import * as Phaser from 'phaser';
import { GameScene }    from './scenes/GameScene';
import { UIScene }      from './scenes/UIScene';
import { setPendingJoin } from './pendingJoin';
import { SHIP_DEFINITIONS } from '@rttv/shared';
import type { ShipId } from '@rttv/shared';
import { getShipVisual } from './shipVisuals';

const phaserConfig: Phaser.Types.Core.GameConfig = {
  type: Phaser.AUTO,
  parent: 'game-container',
  width: window.innerWidth,
  height: window.innerHeight,
  backgroundColor: '#080818',
  render: {
    // Disable bilinear filtering on the WebGL texture sampler.
    // Without this, tightly-packed atlas frames bleed into their neighbours
    // at fractional UV coordinates, producing a 1-px halo around every sprite.
    antialias: false,
    antialiasGL: false,
    roundPixels: true,
  },
  physics: {
    default: 'arcade',
    arcade: {
      debug: false,
      gravity: { x: 0, y: 0 },
    },
  },
  scene: [GameScene, UIScene],
  scale: {
    mode: Phaser.Scale.RESIZE,
    autoCenter: Phaser.Scale.CENTER_BOTH,
  },
};

function startGame(): void {
  document.getElementById('lobby-overlay')?.remove();
  document.getElementById('version-badge')?.remove();
  new Phaser.Game(phaserConfig);
}

// ---------------------------------------------------------------------------
// URL-param bypass — keeps ?name=X&shipId=Y testing shortcut working
// ---------------------------------------------------------------------------
// Populate version badge
const versionBadge = document.getElementById('version-badge');
if (versionBadge) {
  const feVer = __CLIENT_VERSION__;
  versionBadge.textContent = `FE ${feVer}  BE …`;
  fetch('/api/version')
    .then((r) => r.json() as Promise<{ version: string }>)
    .then(({ version }) => { versionBadge.textContent = `FE ${feVer}  BE ${version}`; })
    .catch(() => { versionBadge.textContent = `FE ${feVer}  BE ?`; });
}

const urlParams = new URLSearchParams(window.location.search);
if (urlParams.has('name') && urlParams.has('shipId')) {
  startGame();
} else {
  initLobby();
}

// ---------------------------------------------------------------------------
// Lobby
// ---------------------------------------------------------------------------

function initLobby(): void {
  // Derive lobby bars from the actual definitions so balance changes cannot
  // leave the ship-select screen lying to players.
  type LobbyStat = 'armor' | 'speed' | 'thrust' | 'shield';
  const definitions = Object.values(SHIP_DEFINITIONS);
  const maxima: Record<LobbyStat, number> = {
    armor:  Math.max(...definitions.map((def) => def.armor)),
    speed:  Math.max(...definitions.map((def) => def.speed)),
    thrust: Math.max(...definitions.map((def) => def.thrust)),
    shield: Math.max(...definitions.map((def) => def.shield)),
  };

  document.querySelectorAll<HTMLElement>('.ship-card[data-ship]').forEach(card => {
    const shipId = card.dataset['ship'] as ShipId;
    const def = SHIP_DEFINITIONS[shipId];
    if (!def) return;
    const portrait = card.querySelector<HTMLImageElement>('.ship-icon-wrap img');
    if (portrait) portrait.src = getShipVisual(shipId).assetUrl;
    card.querySelectorAll<HTMLElement>('.stat-bar[data-type]').forEach(bar => {
      const stat   = bar.dataset['type'] as LobbyStat;
      const value  = def[stat];
      const filled = Math.max(1, Math.round(value / maxima[stat] * 10));
      bar.classList.add(stat);
      bar.title = `${stat.toUpperCase()}: ${value}`;
      bar.innerHTML = Array.from({ length: 10 }, (_, i) =>
        `<span class="u${i < filled ? ' on' : ''}"></span>`
      ).join('');
    });
  });

  const overlay     = document.getElementById('lobby-overlay')!;
  const nameInput   = document.getElementById('pilot-name')      as HTMLInputElement;
  const nameError   = document.getElementById('name-error')!;
  const nameLenSpan = document.getElementById('name-len')!;
  const shipCards   = Array.from(document.querySelectorAll<HTMLElement>('.ship-card'));
  const voidList    = document.getElementById('void-list')!;
  const enterBtn    = document.getElementById('enter-void-btn')  as HTMLButtonElement;
  const refreshBtn  = document.getElementById('refresh-rooms')!;

  let selectedShip   = 'fighter';
  let selectedRoomId: string | null = null;

  // ---- Ship selection ----
  shipCards.forEach(card => {
    card.addEventListener('click', () => {
      shipCards.forEach(c => c.classList.remove('selected'));
      card.classList.add('selected');
      selectedShip = card.dataset['ship'] ?? 'fighter';
    });
  });

  // ---- Name input ----
  nameInput.addEventListener('input', () => {
    nameLenSpan.textContent = String(nameInput.value.length);
    updateBtn();
  });

  function validateName(): boolean {
    const val = nameInput.value.trim();
    if (!val)          { nameError.textContent = 'CALL SIGN REQUIRED';                return false; }
    if (val.length < 2){ nameError.textContent = 'MIN 2 CHARACTERS';                  return false; }
    if (!/^[\w\-. ]+$/.test(val)) { nameError.textContent = 'LETTERS, NUMBERS, _ - . SPACE'; return false; }
    nameError.textContent = '';
    return true;
  }

  function updateBtn(): void {
    enterBtn.disabled = !validateName() || !selectedRoomId;
  }

  // ---- Void listing ----
  interface RoomEntry {
    roomId: string;
    clients: number;
    maxClients: number;
    metadata?: { roomName?: string; botCount?: number; minimumPlayers?: number; roundDuration?: number; difficulty?: string; obstacleDensity?: number };
  }

  async function fetchRooms(): Promise<void> {
    voidList.innerHTML = '<div class="void-scanning">SCANNING VOID\u2026</div>';
    selectedRoomId = null;
    updateBtn();
    try {
      const resp = await fetch('/api/rooms');
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const rooms = await resp.json() as RoomEntry[];

      if (rooms.length === 0) {
        voidList.innerHTML =
          '<div class="void-empty">NO ACTIVE VOIDS &mdash; <a href="/manage.html" target="_blank">CREATE ONE</a></div>';
        return;
      }

      voidList.innerHTML = '';
      for (const room of rooms) {
        const name  = room.metadata?.roomName ?? `Void ${room.roomId.slice(0, 6).toUpperCase()}`;
        const entry = document.createElement('div');
        const meta = room.metadata ?? {};
        const dur = meta.roundDuration ? `${meta.roundDuration / 60} MIN` : null;
        const density = meta.obstacleDensity === 0 ? 'NONE' : meta.obstacleDensity === 0.25 ? 'SPARSE' : meta.obstacleDensity === 0.5 ? 'MODERATE' : meta.obstacleDensity === 1 ? 'DENSE' : null;
        const diff = meta.difficulty?.toUpperCase() ?? null;
        const settingsParts = [dur, density, diff].filter(Boolean);
        entry.className = 'void-entry';
        entry.innerHTML = `
          <div class="void-info">
            <span class="void-name">${escHtml(name)}</span>
            ${settingsParts.length ? `<span class="void-settings">${settingsParts.join(' \u00b7 ')}</span>` : ''}
          </div>
          <span class="void-meta">
            <span class="void-players">${room.clients === 0 ? (meta.minimumPlayers ?? 0) : room.clients + (meta.botCount ?? 0)}\u2009/\u2009${room.maxClients} PILOTS</span>
          </span>`;
        entry.addEventListener('click', () => {
          document.querySelectorAll('.void-entry').forEach(v => v.classList.remove('selected'));
          entry.classList.add('selected');
          selectedRoomId = room.roomId;
          updateBtn();
        });
        voidList.appendChild(entry);
      }
    } catch {
      voidList.innerHTML =
        '<div class="void-error">SERVER UNREACHABLE \u2014 IS THE GAME SERVER RUNNING?</div>';
    }
  }

  refreshBtn.addEventListener('click', () => { void fetchRooms(); });
  void fetchRooms();

  // ---- Enter void ----
  enterBtn.addEventListener('click', () => {
    if (!validateName() || !selectedRoomId) return;
    setPendingJoin({
      displayName: nameInput.value.trim().toUpperCase(),
      shipId:      selectedShip,
      roomId:      selectedRoomId,
    });
    overlay.classList.add('fade-out');
    setTimeout(startGame, 440);
  });
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function escHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
